import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import * as AuthService from 'common/services/AuthService';
import { MotiliToken } from 'common/types/access-token';

import { LatestAcknowledgement, State } from './interfaces';

export const initialState: State = {
    authenticated: false,
    client: null,
    clientId: undefined,
    contractor: null,
    contractorId: undefined,
    contractorTeamMemberId: undefined,
    fullName: '',
    initialized: false,
    isClientAccount: false,
    isContractorAccount: false,
    isMotiliAccount: false,
    isMotiliAdmin: false,
    isMotiliApFinanceDept: false,
    isMotiliContractorRelations: false,
    isMotiliEngineeringDept: false,
    isMotiliFinanceDept: false,
    isMotiliManager: false,
    isMotiliOwner: false,
    isMotiliProjectManager: false,
    latestAcknowledgement: {
        acknowledged: false,
        type: '',
        version: 0,
    },
    loading: false,
    token: {
        created: '',
        id: '',
        lastCheckFromBackend: 0,
        ttl: 0,
        user: null,
        userId: 0,
    },
    userName: '',
};

export const authenticationSlice = createSlice({
    name: 'authentication',
    initialState,
    reducers: {
        setAuthentication: (
            state: State,
            action: PayloadAction<MotiliToken>
        ) => {
            const token = action.payload;

            state.initialized = true;
            state.authenticated = true;
            state.loading = false;
            state.token = token;

            state.latestAcknowledgement =
                AuthService.latestAcknowledgment() as LatestAcknowledgement;

            setRoleInfo(state);
            setUserInfo(state);
        },

        setLoading: (state: State, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },

        updateToken: (state: State, action: PayloadAction<MotiliToken>) => {
            state.token = action.payload;
        },

        updateUserAccount: (state: State, action) => {
            const updatedAccountInfo = {
                ...state.token.user,
                ...action.payload,
            };

            state.initialized = true;
            state.authenticated = true;

            state.token = { ...state.token, user: updatedAccountInfo };
            state.loading = false;

            setRoleInfo(state);
            setUserInfo(state);
        },

        acceptAcknowledgement: (state: State) => {
            state.latestAcknowledgement =
                AuthService.latestAcknowledgment() as LatestAcknowledgement;
        },

        resetAuthentication: (state: State) => {
            state.loading = false;
            state.authenticated = false;
            state.initialized = false;
        },
    },
});

export const {
    acceptAcknowledgement,
    resetAuthentication,
    setAuthentication,
    updateUserAccount,
    updateToken,
    setLoading,
} = authenticationSlice.actions;

export default authenticationSlice.reducer;

const setRoleInfo = (state: State) => {
    state.isClientAccount = AuthService.isClientAccount();
    state.isContractorAccount = AuthService.isContractorAccount();

    state.isMotiliAccount = AuthService.isMotiliAccount();
    state.isMotiliAdmin = AuthService.isMotiliAdmin();
    state.isMotiliApFinanceDept = AuthService.isMotiliApFinanceDept();
    state.isMotiliContractorRelations =
        AuthService.isMotiliContractorRelations();
    state.isMotiliEngineeringDept = AuthService.isMotiliEngineeringDept();
    state.isMotiliFinanceDept = AuthService.isMotiliFinanceDept();
    state.isMotiliManager = AuthService.isMotiliManager();
    state.isMotiliOwner = AuthService.isMotiliOwner();
    state.isMotiliProjectManager = AuthService.isMotiliProjectManager();
};

const setUserInfo = (state: State) => {
    state.client = AuthService.getClient() as State['client'];
    state.clientId = AuthService.getClientId();
    state.contractor = AuthService.getContractor();
    state.contractorId = AuthService.getContractorId();
    state.contractorTeamMemberId = AuthService.getContractorTeamMemberId();

    state.userName = AuthService.getAccountUserName();
    state.fullName = AuthService.getAccountFullName();
};
